/* eslint-disable react/jsx-no-bind */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "~/components/shared/DropdownMenu";
import Icon from "~/components/shared/Icon";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";

const Option = ({
  children,
  onChange,
  checked,
  disabled = false,
}: {
  children: React.ReactNode;
  onChange: () => void;
  checked: boolean;
  disabled?: boolean;
}) => {
  return (
    <label className="flex cursor-pointer items-center gap-1.5">
      <input
        type="radio"
        className="peer !m-0"
        onChange={onChange}
        checked={checked}
        disabled={disabled}
      />
      <span>{children}</span>
    </label>
  );
};

export function PipelineSettingsMenu() {
  const { build } = useBuild();
  const { pipeline } = useParams<{ pipeline: string }>();

  const defaultView = useBuildPreferencesStore((state) => state.defaultView);
  const setDefaultView = useBuildPreferencesStore((state) => state.setDefaultView);

  const [updatedAllDefaults, setUpdatedAllDefaults] = useState(false);

  const currentValue = useMemo(() => {
    if (typeof defaultView === "string") {
      return defaultView as BuildView;
    }
    return defaultView[pipeline || ""] || defaultView["*"] || BuildView.Canvas;
  }, [defaultView, pipeline]);

  const setDefaultForAllPipelines = useCallback(() => {
    setDefaultView(currentValue);
    setUpdatedAllDefaults(true);
  }, [currentValue, setDefaultView, setUpdatedAllDefaults]);

  useEffect(() => {
    setUpdatedAllDefaults(false);
  }, [currentValue]);

  useEffect(() => {
    if (updatedAllDefaults) {
      setTimeout(() => {
        setUpdatedAllDefaults(false);
      }, 2000);
    }
  }, [updatedAllDefaults]);

  const isStepsView = useCallback((view: BuildView) => {
    const stepsViews = [BuildView.Canvas, BuildView.Table, BuildView.Waterfall];
    return stepsViews.includes(view);
  }, []);

  const canEdit = build.project.permissions.edit?.allowed;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        title="Pipeline settings"
        className="btn-small flex items-center justify-center rounded-sm border border-slate-400 px-1.5 font-medium shadow-sm transition-all hover:border-slate-500 hover:bg-gray-100 active:bg-gray-300 active:shadow-inner"
      >
        <Icon icon="heroicons/outline/cog-6-tooth" className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent className="min-w-[220px] px-0 py-0">
          {canEdit && (
            <>
              <div className="px-1.5 py-1">
                <DropdownMenuItem asChild={true}>
                  <a
                    href={`${build.project.url}/settings`}
                    className="flex items-center gap-1 rounded-md px-2 py-2.5 font-medium text-inherit hover:bg-purple-100 hover:text-purple-600 hover:no-underline focus:text-inherit"
                  >
                    <Icon icon="heroicons/outline/cog-6-tooth" className="size-4" />
                    Pipeline settings
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild={true}>
                  <a
                    href={`${build.project.url}/settings/steps`}
                    className="flex items-center gap-1 rounded-md px-2 py-2.5 font-medium text-inherit hover:bg-purple-100 hover:text-purple-600 hover:no-underline focus:text-inherit"
                  >
                    <Icon icon="heroicons/outline/code-bracket-square" className="size-4" />
                    Edit steps
                  </a>
                </DropdownMenuItem>
              </div>
              <div className="border-t border-gray-300" />
            </>
          )}

          {!Features.DefaultToAnnotationsTab && (
            <>
              <div className="px-4 py-2" onClick={(event) => event.stopPropagation()}>
                <h4 className="mt-0 text-xs">Default view</h4>

                <div className="mt-2 flex flex-col gap-2 text-sm">
                  <Option
                    checked={currentValue === BuildView.Summary}
                    onChange={() => setDefaultView(BuildView.Summary, pipeline)}
                  >
                    Summary
                  </Option>

                  <Option
                    checked={isStepsView(currentValue)}
                    onChange={() =>
                      !isStepsView(currentValue) && setDefaultView(BuildView.Canvas, pipeline)
                    }
                  >
                    Steps
                  </Option>

                  {isStepsView(currentValue) && (
                    <div className="ml-1.5 flex flex-auto justify-between gap-3 border-l border-gray-300 pl-3">
                      <Option
                        checked={currentValue === BuildView.Canvas}
                        onChange={() => setDefaultView(BuildView.Canvas, pipeline)}
                        disabled={Features.DisableStepsCanvasView}
                      >
                        Canvas
                      </Option>
                      <Option
                        checked={currentValue === BuildView.Table}
                        onChange={() => setDefaultView(BuildView.Table, pipeline)}
                      >
                        Table
                      </Option>
                      <Option
                        checked={currentValue === BuildView.Waterfall}
                        onChange={() => setDefaultView(BuildView.Waterfall, pipeline)}
                        disabled={Features.DisableStepsWaterfallView}
                      >
                        Waterfall
                      </Option>
                    </div>
                  )}

                  <Option
                    checked={currentValue === BuildView.Tests}
                    onChange={() => setDefaultView(BuildView.Tests, pipeline)}
                  >
                    Tests
                  </Option>
                  <Option
                    checked={currentValue === BuildView.Annotations}
                    onChange={() => setDefaultView(BuildView.Annotations, pipeline)}
                  >
                    Annotations
                  </Option>
                </div>
              </div>

              <div className="mb-2 border-t border-gray-300" />

              <div className="px-2 pb-2">
                <button
                  className="shadow-xs flex items-center rounded-md border border-slate-400 px-2 py-0.5 text-sm hover:bg-gray-100"
                  onClick={setDefaultForAllPipelines}
                >
                  Apply to all pipelines
                  {updatedAllDefaults && (
                    <Icon icon="heroicons/outline/check" className="ml-2 h-4 w-4 text-green-500" />
                  )}
                </button>
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
